import { Country, State, City } from "./types";
import {
  API_BASE_URL,
  getHeaders,
  FALLBACK_COUNTRIES,
  PUERTO_RICO_STATE,
  POPULAR_COUNTRIES,
} from "./constants/config";
import { translateToSpanish } from "./constants/translations";
import { isValidCityName } from "./utils/validation";
import { US_SUBDIVISIONS } from "../../constants/locations/supported";

/**
 * Return the locally supported countries.
 */
export const getCountries = async (): Promise<Country[]> => [
  ...FALLBACK_COUNTRIES,
];

/**
 * Return the locally supported subdivisions for a country.
 */
export const getStatesByCountry = async (
  countryIso2: string
): Promise<State[]> => {
  if (countryIso2 === "PR") {
    return [{ ...PUERTO_RICO_STATE }];
  }

  if (countryIso2 !== "US") {
    throw new Error(`Unsupported country code: ${countryIso2}`);
  }

  return US_SUBDIVISIONS.map((state, index) => ({
    id: index + 1,
    name: translateToSpanish(state.name, "states"),
    country_id: FALLBACK_COUNTRIES[1].id,
    country_code: "US",
    country_name: FALLBACK_COUNTRIES[1].name,
    state_code: state.code,
    iso2: state.code,
    type: "state",
    latitude: "",
    longitude: "",
  })).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Fetch cities for a specific state in a country
 */
export const getCitiesByState = async (
  countryIso2: string,
  stateIso2: string
): Promise<City[]> => {
  try {
    // Handle Puerto Rico specially - fetch from US/PR in the API
    if (countryIso2 === "PR" && stateIso2 === "PR") {
      if (!process.env.REACT_APP_COUNTRY_STATE_CITY_API_KEY) {
        throw new Error("CountryStateCityAPI key is not configured");
      }

      // Puerto Rico cities are available under US country with PR state code
      const apiUrl = `${API_BASE_URL}/countries/US/states/PR/cities`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cities: City[] = await response.json();

      // Map the cities to have Puerto Rico as country instead of US
      const mappedCities = cities.map((city) => ({
        ...city,
        state_code: "PR",
        state_name: "Puerto Rico",
        country_code: "PR",
        country_name: "Puerto Rico",
      }));

      // Apply filtering to remove neighborhoods, districts, and non-city entries
      const filteredCities = mappedCities.filter((city) =>
        isValidCityName(city.name, "PR", "PR")
      );

      // Translate city names to Spanish
      const translatedCities = filteredCities.map((city) => ({
        ...city,
        name: translateToSpanish(city.name, "cities"),
        state_name: "Puerto Rico",
        country_name: "Puerto Rico",
      }));

      return translatedCities.sort((a, b) => a.name.localeCompare(b.name));
    }

    if (!process.env.REACT_APP_COUNTRY_STATE_CITY_API_KEY) {
      throw new Error("CountryStateCityAPI key is not configured");
    }

    // Log the exact API URL being called for debugging
    const apiUrl = `${API_BASE_URL}/countries/${countryIso2}/states/${stateIso2}/cities`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const cities: City[] = await response.json();

    // Map the API fields to our expected format
    const mappedCities = cities.map((city) => ({
      ...city,
      // Add state_code if it's missing
      state_code: city.state_code || stateIso2,
      // Ensure we have country_code
      country_code: city.country_code || countryIso2,
    }));

    // Apply filtering to remove neighborhoods, districts, and non-city entries
    const originalCount = mappedCities.length;
    const filteredCities = mappedCities.filter((city) =>
      isValidCityName(city.name, countryIso2, stateIso2)
    );

    // Log filtering results for debugging
    if (filteredCities.length !== originalCount) {
      // Log some examples of filtered out entries for debugging
      const filtered = mappedCities.filter(
        (city) => !isValidCityName(city.name, countryIso2, stateIso2)
      );
      if (filtered.length > 0) {
      }
    }

    // Translate city names to Spanish
    const translatedCities = filteredCities.map((city) => ({
      ...city,
      name: translateToSpanish(city.name, "cities"),
      state_name: translateToSpanish(city.state_name, "states"),
      country_name: translateToSpanish(city.country_name, "countries"),
    }));

    return translatedCities.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Error fetching cities:", error);

    // No fallback data available, throw the error
    console.error(
      `Failed to fetch cities for ${countryIso2}/${stateIso2}:`,
      error
    );

    throw error;
  }
};

/**
 * Helper function to get popular countries (limited to Puerto Rico and USA)
 */
export const getPopularCountries = (): string[] => {
  return POPULAR_COUNTRIES;
};
